import { configureStore as createStore } from '@reduxjs/toolkit'
import createSagaMiddleware from 'redux-saga'
import type {
  Action,
  ConfigureStoreOptions,
  Dispatch,
  Middleware,
  StoreEnhancer,
  ThunkMiddleware,
  Tuple,
  UnknownAction
} from '@reduxjs/toolkit'
import type { Saga, SagaMiddleware } from 'redux-saga'

export interface ConfigureStoreProps<
  S,
  A extends Action = UnknownAction,
  M extends Tuple<ReadonlyArray<Middleware<object, S>>> = Tuple<
    ReadonlyArray<ThunkMiddleware<S, A>>
  >,
  E extends Tuple<ReadonlyArray<StoreEnhancer>> = Tuple<ReadonlyArray<StoreEnhancer>>
> extends Omit<ConfigureStoreOptions<S, A, M, E>, 'middleware'> {
  middleware?: Array<Middleware<object, S, Dispatch<A>> | SagaMiddleware<object>>
  sagaActionWatcher?: Saga
}

const configureStore = <
  S = unknown,
  A extends Action = UnknownAction,
  M extends Tuple<ReadonlyArray<Middleware<object, S>>> = Tuple<
    ReadonlyArray<ThunkMiddleware<S, A>>
  >,
  E extends Tuple<ReadonlyArray<StoreEnhancer>> = Tuple<ReadonlyArray<StoreEnhancer>>
>({
  middleware = [],
  sagaActionWatcher,
  ...configureStoreProps
}: ConfigureStoreProps<S, A, M, E>) => {
  const rootMiddlewares = [...middleware]
  const sagaMiddleware = createSagaMiddleware()
  if (sagaActionWatcher) {
    rootMiddlewares.push(sagaMiddleware)
  }

  const store = createStore({
    ...(configureStoreProps as ConfigureStoreOptions),
    middleware: (getDefaultMiddleware) => getDefaultMiddleware().concat(rootMiddlewares)
  })

  if (sagaActionWatcher) {
    sagaMiddleware.run(sagaActionWatcher)
  }

  return store
}

export type SliceActions<T> = {
  [K in keyof T]: T[K] extends (...args: unknown[]) => infer A ? A : never
}[keyof T]

export default configureStore
